import { defineStore } from 'pinia';
import { computed, reactive, ref } from 'vue';
import type { SurgeonOption } from '@/api/caseSettings';
import { loadMySurgeons } from '@/api/surgeons';
import { useUserStore } from '@/stores/userStore';
import { DataState, makeDataState, withStateLoading } from '@/util/dataState';

/**
 * Which surgeon the preferences page is editing.
 *
 * A singleton store fits: the page edits one surgeon at a time. A caller who sees exactly one
 * surgeon has them selected automatically. A surgeon whose only subject is themselves is editing
 * their own preferences and has nothing to choose, so the page offers them no selector; anyone
 * setting a surgeon up is always shown whose preferences they are on.
 */
export const usePreferencesSubject = defineStore('preferences-subject', () => {
    const currentUser = useUserStore();
    const options = ref<SurgeonOption[]>([]);
    const selectedId = ref<number | null>(null);
    const _stateHolder = reactive(makeDataState());

    const isReady = computed(() => _stateHolder.state !== DataState.Loading);
    const loadFailed = computed(() => _stateHolder.state === DataState.Error);
    const hasOptions = computed(() => options.value.length > 0);

    /**
     * The caller's only subject is the caller.
     *
     * Read from the subject list rather than from a role, because a surgeon who also administers
     * their organisation acts for every surgeon in it and does have a choice to make.
     */
    const isEditingSelf = computed(() => {
        const callerId = currentUser.user?.id;
        return options.value.length === 1 && options.value[0].id === callerId;
    });

    function select(id: number | null): void {
        selectedId.value = id;
    }

    async function load(): Promise<void> {
        await withStateLoading(_stateHolder, async () => {
            options.value = await loadMySurgeons();
            selectedId.value = options.value.length === 1 ? options.value[0].id : null;
        });
    }

    return {
        options,
        selectedId,
        hasOptions,
        isEditingSelf,
        isReady,
        loadFailed,
        load,
        select,
    };
});
